import { useMemo } from 'react';
import {
  Badge,
  Input,
  Textarea,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { Alert } from '@vnetwork/ui/feedback';
import type {
  ConfigImage,
  MarketplaceApp,
  MarketplaceField,
} from '@/api/opennebula';
import {
  SectionCard,
  pickCard,
  distroCard,
  type DistroGroup,
  type CreateState,
} from './shared';

interface OsSectionProps {
  form: CreateState;
  patch: (p: Partial<CreateState>) => void;
  distroGroups: DistroGroup[];
  distributions: ConfigImage[];
  customImages: ConfigImage[];
  apps: MarketplaceApp[];
  activeDistro: string;
  selectedApp?: MarketplaceApp;
  appFieldDefs: MarketplaceField[];
  pickImage: (img: ConfigImage) => void;
  pickDistro: (distro: string) => void;
  pickApp: (app: MarketplaceApp) => void;
}

/** Instance Operating System — distributions / custom image / marketplace. */
export function OperatingSystemCard({
  form,
  patch,
  distroGroups,
  distributions,
  customImages,
  apps,
  activeDistro,
  selectedApp,
  appFieldDefs,
  pickImage,
  pickDistro,
  pickApp,
}: OsSectionProps) {
  const isMarketplace = form.osTab === 'marketplace';
  return (
    <SectionCard
      title="Instance Operating System"
      description="You can choose the operating system for your instance to suit your specific needs."
    >
      <Tabs
        value={form.osTab}
        onValueChange={(v) => patch({ osTab: v as CreateState['osTab'], image: '', marketplaceApp: '' })}
      >
        <TabsList>
          <TabsTrigger value="distributions">Distributions</TabsTrigger>
          <TabsTrigger value="custom">Custom Image</TabsTrigger>
          <TabsTrigger value="marketplace">Marketplace</TabsTrigger>
        </TabsList>
        <TabsContent value="distributions">
          <DistroPicker
            groups={distroGroups}
            activeDistro={activeDistro}
            selectedImage={form.image}
            onPickDistro={pickDistro}
            onPickVersion={(id) => {
              const img = distributions.find((i) => String(i.id) === id);
              if (img) pickImage(img);
            }}
          />
        </TabsContent>
        <TabsContent value="custom">
          <CustomImagePicker images={customImages} value={form.image} onPick={pickImage} />
        </TabsContent>
        <TabsContent value="marketplace">
          <AppPicker apps={apps} value={form.marketplaceApp} onPick={pickApp} />
          {isMarketplace && selectedApp && appFieldDefs.length > 0 && (
            <div style={{ marginTop: 'var(--vn-space-5)' }}>
              <AppConfig
                fields={appFieldDefs}
                values={form.appFields}
                onChange={(appFields) => patch({ appFields })}
              />
            </div>
          )}
        </TabsContent>
      </Tabs>
    </SectionCard>
  );
}

function DistroPicker({
  groups,
  activeDistro,
  selectedImage,
  onPickDistro,
  onPickVersion,
}: {
  groups: DistroGroup[];
  activeDistro: string;
  selectedImage: string;
  onPickDistro: (d: string) => void;
  onPickVersion: (id: string) => void;
}) {
  if (!groups.length)
    return (
      <Alert tone="info" style={{ marginTop: 'var(--vn-space-3)' }}>
        No distributions available.
      </Alert>
    );
  const active = groups.find((g) => g.distro === activeDistro) ?? groups[0];
  return (
    <div style={{ marginTop: 'var(--vn-space-3)' }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))', gap: 'var(--vn-space-2)' }}>
        {groups.map((g) => {
          const sel = g.distro === active.distro;
          const slug = g.distro.toLowerCase();
          return (
            <button key={g.distro} type="button" onClick={() => onPickDistro(g.distro)} style={distroCard(sel)}>
              <img
                src={`https://upload.vnetwork.vn/assets/os_images/${slug}.svg`}
                alt=""
                width={36}
                height={36}
                style={{ objectFit: 'contain' }}
                onError={(e) => {
                  (e.currentTarget as HTMLImageElement).style.visibility = 'hidden';
                }}
              />
              <span style={{ fontSize: 'var(--vn-font-size-sm)', fontWeight: 500 }}>{g.distro}</span>
            </button>
          );
        })}
      </div>
      <div style={{ marginTop: 'var(--vn-space-4)', maxWidth: 420 }}>
        <Field label={`${active.distro} version`}>
          {() => (
            <Select value={selectedImage} onValueChange={onPickVersion}>
              <SelectTrigger>
                <SelectValue placeholder="Select a version" />
              </SelectTrigger>
              <SelectContent>
                {active.images.map((img) => (
                  <SelectItem key={img.id} value={String(img.id)}>
                    {img.name || img.version || img.id}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </Field>
      </div>
    </div>
  );
}

function CustomImagePicker({
  images,
  value,
  onPick,
}: {
  images: ConfigImage[];
  value: string;
  onPick: (img: ConfigImage) => void;
}) {
  if (!images.length)
    return (
      <Alert tone="info" style={{ marginTop: 'var(--vn-space-3)' }}>
        No custom images. Create one from an instance snapshot.
      </Alert>
    );
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 'var(--vn-space-3)', marginTop: 'var(--vn-space-3)' }}>
      {images.map((img) => {
        const sel = String(img.id) === value;
        return (
          <button key={img.id} type="button" onClick={() => onPick(img)} style={pickCard(sel)}>
            <div style={{ fontWeight: 600 }}>{img.name || img.id}</div>
            <div style={{ color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)' }}>{img.distro || img.version || '—'}</div>
            {img.volume ? (
              <Badge tone="neutral" appearance="subtle" size="sm">
                {img.volume} GiB
              </Badge>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}

function AppPicker({
  apps,
  value,
  onPick,
}: {
  apps: MarketplaceApp[];
  value: string;
  onPick: (app: MarketplaceApp) => void;
}) {
  if (!apps.length)
    return (
      <Alert tone="info" style={{ marginTop: 'var(--vn-space-3)' }}>
        No marketplace applications available.
      </Alert>
    );
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 'var(--vn-space-3)', marginTop: 'var(--vn-space-3)' }}>
      {apps.map((app) => {
        const sel = app.name === value;
        return (
          <button key={app.name} type="button" onClick={() => onPick(app)} style={pickCard(sel)}>
            <div style={{ fontWeight: 600 }}>{app.distro || app.name}</div>
            <div style={{ color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)' }}>{app.provisioner || 'application'}</div>
          </button>
        );
      })}
    </div>
  );
}

function AppConfig({
  fields,
  values,
  onChange,
}: {
  fields: MarketplaceField[];
  values: Record<string, string>;
  onChange: (v: Record<string, string>) => void;
}) {
  const set = (k: string, val: string) => onChange({ ...values, [k]: val });
  const groups = useMemo(() => {
    const m = new Map<string, MarketplaceField[]>();
    fields.forEach((f) => {
      const g = f.group ?? '';
      m.set(g, [...(m.get(g) ?? []), f]);
    });
    return [...m.entries()];
  }, [fields]);
  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-5)' }}>
      {groups.map(([group, items]) => (
        <div key={group || 'main'} style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
          {group && <div style={{ fontWeight: 600, fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-muted)' }}>{group}</div>}
          {items.map((f) => {
            const val = values[f.key] ?? f.value ?? '';
            return (
              <Field key={f.key} label={f.key} required={f.required !== false && f.type !== 'select'} description={f.helper}>
                {(p) =>
                  f.type === 'select' ? (
                    <Select value={val} onValueChange={(v) => set(f.key, v)}>
                      <SelectTrigger style={{ maxWidth: 420 }}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {(f.options ?? []).map((o) => (
                          <SelectItem key={o.value} value={o.value}>
                            {o.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : f.helper && f.helper.length > 80 ? (
                    <Textarea {...p} value={val} onChange={(e) => set(f.key, e.target.value)} rows={2} />
                  ) : (
                    <Input {...p} value={val} onChange={(e) => set(f.key, e.target.value)} style={{ maxWidth: 420 }} />
                  )
                }
              </Field>
            );
          })}
        </div>
      ))}
    </div>
  );
}
